'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { setApiAuthAdapter } from '@/lib/api-client';
import { AppError } from '@/lib/error';

import {
  AuthAdapter,
  AuthCapabilities,
  AuthFlowType,
  AuthState,
  AuthUser,
  CredentialsInput,
} from '../lib/auth-adapter';
import { createAuthAdapter } from '../lib/create-adapter';

export type AuthContextValue = {
  // State
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AppError | null;

  // Adapter info
  flowType: AuthFlowType;
  adapterName: string;
  capabilities: AuthCapabilities;

  // Actions
  signIn: (credentials?: CredentialsInput) => Promise<void>;
  signUp: (credentials: CredentialsInput) => Promise<AuthState>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;

  // Token access for API clients
  getAccessToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function useAuthAdapter() {
  const adapterRef = useRef<AuthAdapter>(null);
  if (!adapterRef.current) {
    adapterRef.current = createAuthAdapter();
  }
  return adapterRef.current;
}

export type AuthProviderProps = {
  children: React.ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const adapter = useAuthAdapter();
  const [authState, setAuthState] = useState<AuthState>(() => adapter.getAuthState());
  const initialized = useRef(false);

  useEffect(() => {
    const unsubscribe = adapter.onAuthStateChange((state) => {
      setAuthState(state);
    });

    if (!initialized.current) {
      initialized.current = true;

      // Make sure we only set it after the adapter is initialized
      adapter.initialize().then(() => {
        setApiAuthAdapter(adapter);
      });
    }

    return unsubscribe;
  }, [adapter]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: authState.user,
      isAuthenticated: authState.status === 'authenticated',
      isLoading: authState.status === 'loading' || authState.status === 'idle',
      error: authState.error,
      flowType: adapter.flowType,
      adapterName: adapter.name,
      capabilities: adapter.capabilities,
      signIn: adapter.signIn,
      signUp: adapter.signUp,
      signOut: adapter.signOut,
      resetPassword: adapter.resetPassword,
      changePassword: adapter.changePassword,
      getAccessToken: adapter.getAccessToken,
    }),
    [
      authState.user,
      authState.status,
      authState.error,
      adapter.flowType,
      adapter.name,
      adapter.capabilities,
      adapter.signIn,
      adapter.signUp,
      adapter.signOut,
      adapter.resetPassword,
      adapter.changePassword,
      adapter.getAccessToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
